import '../App.css';
import '../style.css';
import '../font.css';
import '../workDetailPage/style_works.css';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  copyArticleSectionUrlToClipboard,
  slugifyFragmentId,
} from '../utils/articleSectionLink';
import { ReactComponent as IconSlashSemicircle } from '../image/icon_slash_semicircle.svg';
import { useLanguage } from '../contexts/LanguageContext';

const HOVER_TOOLTIP_MS = 500;
const COPIED_TOAST_MS = 2000;

export function ArticleTextSection(props: {
    title?: ReactNode;
    titleEn?: ReactNode;
    /** 명시 id. 없으면 `title`이 문자열일 때만 slug로 자동 생성됩니다. 중복 제목이면 반드시 지정하세요. */
    sectionId?: string;
    subTitle?: ReactNode;
    subTitleEn?: ReactNode;
    description? : React.ReactNode;
    descriptionEn? : React.ReactNode;
    subDescription? : React.ReactNode;
    subDescriptionEn? : React.ReactNode;
    marginTop?: boolean;
    marginBottom?: boolean;
    /**
     * @deprecated 새 코드에서는 titleEn / subTitleEn / descriptionEn / subDescriptionEn 을 사용하세요.
     * (구버전 호환) true이면 언어 토글에 따라 title/description 자리에 subTitle/subDescription(영문)을 보여주고,
     * subTitle/subDescription은 별도 영역으로 노출하지 않는다.
     */
    swapByLanguage?: boolean;
    }) {
    const marginTopStyle = props.marginTop ? '40px' : '0px';
    const marginBottomStyle = props.marginBottom ? '20px' : '0px';

    const { language } = useLanguage();
    const isEn = language === 'en';
    // 구버전 호환: swapByLanguage가 켜진 essay는 subTitle/subDescription을 영어 번역으로 사용한다.
    const useLegacySwap = props.swapByLanguage && isEn;

    const displayTitle = useLegacySwap && props.subTitle
      ? props.subTitle
      : (isEn && props.titleEn ? props.titleEn : props.title);
    const displayDescription = useLegacySwap && props.subDescription
      ? props.subDescription
      : (isEn && props.descriptionEn ? props.descriptionEn : props.description);
    const displaySubTitle = isEn && props.subTitleEn ? props.subTitleEn : props.subTitle;
    const displaySubDescription =
      isEn && props.subDescriptionEn ? props.subDescriptionEn : props.subDescription;

    // 구버전 swapByLanguage에서는 subTitle/subDescription이 title 자리로 흡수되므로 별도 노출 안 함.
    const showSubTitle = !props.swapByLanguage && displaySubTitle;
    const showSubDescription = !props.swapByLanguage && displaySubDescription;

    const domSectionId =
      props.sectionId?.trim() ||
      (typeof props.title === 'string' && props.title.trim()
        ? slugifyFragmentId(props.title)
        : undefined);

    const [longHoverTooltip, setLongHoverTooltip] = useState(false);
    const [copiedToast, setCopiedToast] = useState(false);
    const hoverDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copiedResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearHoverTooltipTimer = useCallback(() => {
      if (hoverDelayRef.current) {
        clearTimeout(hoverDelayRef.current);
        hoverDelayRef.current = null;
      }
    }, []);

    const handleWrapMouseEnter = useCallback(() => {
      clearHoverTooltipTimer();
      hoverDelayRef.current = setTimeout(() => {
        setLongHoverTooltip(true);
      }, HOVER_TOOLTIP_MS);
    }, [clearHoverTooltipTimer]);

    const handleWrapMouseLeave = useCallback(() => {
      clearHoverTooltipTimer();
      setLongHoverTooltip(false);
    }, [clearHoverTooltipTimer]);

    const handleCopySectionLink = useCallback(async () => {
      if (!domSectionId) return;
      clearHoverTooltipTimer();
      setLongHoverTooltip(false);
      const ok = await copyArticleSectionUrlToClipboard(domSectionId);
      if (ok) {
        if (copiedResetRef.current) clearTimeout(copiedResetRef.current);
        setCopiedToast(true);
        copiedResetRef.current = setTimeout(() => setCopiedToast(false), COPIED_TOAST_MS);
      }
    }, [domSectionId, clearHoverTooltipTimer]);

    const handleTitleRowClick = useCallback(
      (_e: MouseEvent<HTMLDivElement>) => {
        if (!domSectionId) return;
        const sel = window.getSelection();
        if (sel && sel.toString().trim().length > 0) return;
        void handleCopySectionLink();
      },
      [domSectionId, handleCopySectionLink]
    );

    const handleTitleRowKeyDown = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        if (!domSectionId) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          void handleCopySectionLink();
        }
      },
      [domSectionId, handleCopySectionLink]
    );

    useEffect(
      () => () => {
        clearHoverTooltipTimer();
        if (copiedResetRef.current) clearTimeout(copiedResetRef.current);
      },
      [clearHoverTooltipTimer]
    );

    const showTooltip = longHoverTooltip || copiedToast;
    const tooltipLabel = copiedToast ? '복사완료!' : '링크 복사';

    return (
        <>
            <div 
                className={'articletextsection fadeIn'}
                id={domSectionId}
                style={{
                    marginTop: marginTopStyle,
                    marginBottom: marginBottomStyle,
                    scrollMarginTop: '1rem',
                }}
            >
                {displayTitle && (
                  <div
                    className={
                      'articleTitleRow' +
                      (domSectionId ? ' articleTitleRow--interactive' : '')
                    }
                    onClick={domSectionId ? handleTitleRowClick : undefined}
                    onMouseEnter={domSectionId ? handleWrapMouseEnter : undefined}
                    onMouseLeave={domSectionId ? handleWrapMouseLeave : undefined}
                    onKeyDown={domSectionId ? handleTitleRowKeyDown : undefined}
                    role={domSectionId ? 'button' : undefined}
                    tabIndex={domSectionId ? 0 : undefined}
                    aria-label={
                      domSectionId ? '이 섹션 링크 클립보드에 복사' : undefined
                    }
                  >
                    <div className="articleTitle">{displayTitle}</div>
                    {domSectionId && (
                      <div className="articleSectionCopyLinkWrap">
                        <span
                          className="articleSectionCopyLinkBtn"
                          aria-hidden
                        >
                          <span className="articleSectionCopyLinkIcon">
                            <IconSlashSemicircle width={24} height={24} />
                          </span>
                        </span>
                        {showTooltip && (
                          <>
                            <span
                              className="articleSectionCopyLinkHoverPad"
                              aria-hidden
                            />
                            <span
                              className="articleSectionCopyLinkTooltip"
                              role="tooltip"
                            >
                              {tooltipLabel}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className='articleDescriptionContainer'>
                    {showSubTitle && <div className='articleSubTitle'>{displaySubTitle}</div>}
                    <div className='textsection-sub'>
                        {displayDescription && <div className='articleDescription'>{displayDescription}</div>}
                        {showSubDescription && <div className='articleSubdescription' >{displaySubDescription}</div>}
                    </div>
                </div>
                
            </div>
        </>
      )
}
